import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Autorenew, ContentCopy } from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import { Chip } from '@jotpsych/ui';
import { useSnackbar } from 'notistack';

import { useCopyToClipboard } from '@hooks/useCopyToClipboard';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import { MaskedTokenField } from '../components/masked-token-field';
import { useGetMcpAccessQuery, useMintMcpTokenMutation } from '../store/hooks';
import type { McpToolInfo } from '../store/hooks';

function mcpUrl(path: string): string {
  const apiUrl = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  return `${apiUrl}${path}`;
}

/** `claude mcp add` command for registering this server with Claude Code. */
function claudeCodeCliCommand(url: string): string {
  return `claude mcp add --transport http jotpsych-admin ${url} --header "Authorization: Bearer <your token above>"`;
}

export default function McpAccessPage() {
  const navigate = useNavigate();
  const copy = useCopyToClipboard();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error } = useGetMcpAccessQuery();
  const [mintToken, { isLoading: isMinting }] = useMintMcpTokenMutation();
  // The raw secret is only returned at mint time; hold it for this render
  // of the page so the user can copy it. It is never fetchable again.
  const [mintedSecret, setMintedSecret] = useState<string | null>(null);

  const tools = useMemo<McpToolInfo[]>(() => data?.tools ?? [], [data]);
  const [scope, setScope] = useState<'all' | 'read' | 'write'>('all');
  // Scope the next minted token will carry — the user's choice.
  const [mintScope, setMintScope] = useState<'read' | 'write'>('write');

  const handleMint = async () => {
    try {
      const result = await mintToken({ scope: mintScope }).unwrap();
      setMintedSecret(result.token);
      enqueueSnackbar('Token generated — copy it now, it is only shown once', {
        variant: 'success',
      });
    } catch (err) {
      const e = err as { status?: number | string; data?: { message?: string } };
      const detail = e?.data?.message ?? (e?.status ? `HTTP ${e.status}` : null);
      enqueueSnackbar(
        detail ? `Failed to generate token: ${detail}` : 'Failed to generate token',
        { variant: 'error' },
      );
    }
  };

  const visibleTools = useMemo(() => {
    const filtered =
      scope === 'all' ? tools : tools.filter(tool => tool.required_scope === scope);
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [tools, scope]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading MCP access</h2>
            <p className="text-gray-600 mt-2">Unable to fetch MCP connection details.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const url = data ? mcpUrl(data.mcp_path) : '';
  const cliCommand = claudeCodeCliCommand(url);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">MCP Access</h1>
          <p className="text-gray-600 mt-1">
            Connection details for the admin tools MCP server. Add it to Claude Code with the
            command below, using your personal bearer token.
          </p>
        </div>

        {isLoading && !data ? (
          <LoadingState message="Loading MCP access..." />
        ) : (
          <>
            <Card className="mb-6">
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700">MCP URL</div>
                    <div className="mt-1 flex items-center gap-1">
                      <span className="flex-1 break-all font-mono text-xs text-gray-700">
                        {url}
                      </span>
                      <Tooltip title="Copy MCP URL">
                        <IconButton size="small" onClick={() => copy(url, 'MCP URL')}>
                          <ContentCopy fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>

                  {mintedSecret ? (
                    <>
                      <MaskedTokenField
                        label="Your token"
                        hint={`Personal ${data?.user_token?.scope ?? ''} token. Tied to your user id.`}
                        token={mintedSecret}
                      />
                      <Alert severity="warning">
                        Copy this token now — it is stored hashed and cannot be shown again.
                      </Alert>
                    </>
                  ) : data?.user_token ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700">Your token</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-gray-700">
                          {data.user_token.token_prefix}…
                        </span>
                        <Chip
                          label={data.user_token.scope}
                          variant="soft"
                          color={data.user_token.scope === 'write' ? 'warning' : 'default'}
                          size="small"
                        />
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        The full token was shown once when generated. Rotate it if you no longer
                        have it — rotating revokes the current token.
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      You have no active token. Choose an access level and generate one to
                      connect.
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={mintScope}
                      onChange={(_event, value) => value && setMintScope(value)}
                    >
                      <ToggleButton value="write">Read &amp; write</ToggleButton>
                      <ToggleButton value="read">Read only</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleMint}
                      disabled={isMinting}
                      startIcon={
                        isMinting ? (
                          <CircularProgress size={16} />
                        ) : data?.user_token ? (
                          <Autorenew fontSize="small" />
                        ) : (
                          <Add fontSize="small" />
                        )
                      }
                    >
                      {data?.user_token ? 'Rotate token' : 'Generate token'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardContent>
                <div className="text-sm font-medium text-gray-700">Connect to Claude Code</div>
                <p className="mt-1 text-xs text-gray-500">
                  Run this, replacing the placeholder with your token above, then restart
                  Claude Code.
                </p>
                <div className="mt-2 flex items-start gap-1">
                  <pre className="flex-1 whitespace-pre-wrap break-all rounded border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800">
                    {cliCommand}
                  </pre>
                  <Tooltip title="Copy command">
                    <IconButton
                      size="small"
                      onClick={() => copy(cliCommand, 'Claude Code command')}
                    >
                      <ContentCopy fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Tools</h3>
                    <p className="text-sm text-gray-500">
                      {scope === 'all'
                        ? `${tools.length} total`
                        : `${visibleTools.length} of ${tools.length}`}
                    </p>
                  </div>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={scope}
                    onChange={(_event, value) => value && setScope(value)}
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="read">Read</ToggleButton>
                    <ToggleButton value="write">Write</ToggleButton>
                  </ToggleButtonGroup>
                </div>
                {visibleTools.length === 0 ? (
                  <p className="py-8 text-center text-gray-500">No tools available</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {visibleTools.map(tool => (
                      <li key={tool.name} className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono font-medium text-gray-900">
                            {tool.name}
                          </span>
                          <Chip
                            label={tool.required_scope}
                            variant="soft"
                            color={tool.required_scope === 'write' ? 'warning' : 'default'}
                            size="small"
                          />
                          {(tool.tags ?? []).map(tag => (
                            <Chip
                              key={tag}
                              label={tag}
                              variant="soft"
                              color="default"
                              size="small"
                            />
                          ))}
                        </div>
                        {tool.description && (
                          <p className="mt-1 text-sm text-gray-600">{tool.description}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}
